import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import Seo from "@/components/Seo";
import { toast } from "sonner";
import { Stethoscope, BadgeCheck } from "lucide-react";

export default function DoctorRegistrationPage() {
  const navigate = useNavigate();
  const [specialties, setSpecialties] = useState([]);
  const [areas, setAreas] = useState([]);
  const [form, setForm] = useState({
    name: "", phone: "", whatsapp: "", email: "",
    specialty_slug: "", qualification: "", registration_number: "",
    experience_years: 0, gender: "",
    clinic_name: "", address: "", area_slug: "", city_slug: "pune",
    consultation_fee: 500, timings: "", languages: "", services: "",
    photo_url: "", about: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    api.get("/specialties").then((r) => setSpecialties(r.data.items || []));
    api.get("/cities/pune/areas").then((r) => setAreas(r.data.items || []));
  }, []);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    const required = ["name", "phone", "email", "specialty_slug", "qualification", "registration_number", "clinic_name", "address", "area_slug"];
    for (const f of required) if (!form[f]) { toast.error(`Please fill ${f.replace(/_/g, " ")}`); return; }
    setSubmitting(true);
    try {
      await api.post("/doctor-registrations", {
        ...form,
        experience_years: Number(form.experience_years) || 0,
        consultation_fee: Number(form.consultation_fee) || 500,
        languages: form.languages ? form.languages.split(",").map((s) => s.trim()).filter(Boolean) : [],
        services: form.services ? form.services.split(",").map((s) => s.trim()).filter(Boolean) : [],
      });
      setDone(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <section className="mx-auto max-w-[720px] px-4 py-20 text-center">
        <Seo title="Registration submitted" canonicalPath="/list-your-practice" />
        <span className="inline-flex h-16 w-16 rounded-2xl bg-accent text-[hsl(var(--ld-jade-fg))] items-center justify-center">
          <BadgeCheck size={28} />
        </span>
        <h1 className="font-display text-3xl font-bold mt-5">Submitted for review</h1>
        <p className="text-muted-foreground mt-3">
          Thanks for registering with LocalDoctor.in. Our team reviews new profiles within 24–48 hours
          and will reach out at the number you provided.
        </p>
        <button className="btn-primary mt-7" onClick={() => navigate("/")}>Back to home</button>
      </section>
    );
  }

  return (
    <>
      <Seo
        title="List your practice"
        description="Join LocalDoctor.in — list your clinic in Pune and accept queue-less OTP appointments from verified patients near you."
        canonicalPath="/list-your-practice"
      />
      <section className="border-b border-border bg-secondary/40">
        <div className="mx-auto max-w-[1280px] px-4 lg:px-6 py-10 lg:py-14 grid lg:grid-cols-12 gap-6 items-end">
          <div className="lg:col-span-8">
            <span className="inline-flex items-center gap-2 rounded-full bg-accent text-[hsl(var(--ld-jade-fg))] px-3 py-1 text-xs font-semibold">
              <Stethoscope size={14} /> For doctors
            </span>
            <h1 className="font-display text-3xl md:text-4xl font-bold mt-4 text-balance">
              List your practice on LocalDoctor.in
            </h1>
            <p className="text-muted-foreground mt-3 max-w-2xl">
              Get discovered by patients searching for your specialty in Pune. Your profile is reviewed
              and verified by our team before it goes live.
            </p>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-[920px] px-4 lg:px-6 py-10">
        <form onSubmit={submit} className="grid gap-8" data-testid="register-form">
          <fieldset className="grid gap-4">
            <legend className="font-display font-semibold text-lg">About you</legend>
            <div className="grid sm:grid-cols-2 gap-4">
              <input className="input-base" placeholder="Full name *" value={form.name} onChange={update("name")} data-testid="reg-name" />
              <input className="input-base tabular" placeholder="Mobile number *" value={form.phone} onChange={update("phone")} data-testid="reg-phone" />
              <input className="input-base tabular" placeholder="WhatsApp number" value={form.whatsapp} onChange={update("whatsapp")} data-testid="reg-whatsapp" />
              <input className="input-base" placeholder="Email *" type="email" value={form.email} onChange={update("email")} data-testid="reg-email" />
              <select className="input-base" value={form.gender} onChange={update("gender")} data-testid="reg-gender">
                <option value="">Gender (optional)</option>
                <option value="female">Female</option>
                <option value="male">Male</option>
                <option value="other">Prefer not to say</option>
              </select>
              <input className="input-base tabular" type="number" min="0" placeholder="Years of experience" value={form.experience_years} onChange={update("experience_years")} data-testid="reg-exp" />
            </div>
          </fieldset>

          <fieldset className="grid gap-4">
            <legend className="font-display font-semibold text-lg">Qualifications</legend>
            <div className="grid sm:grid-cols-2 gap-4">
              <select className="input-base" value={form.specialty_slug} onChange={update("specialty_slug")} data-testid="reg-specialty">
                <option value="">Specialty *</option>
                {specialties.map((s) => <option key={s.slug} value={s.slug}>{s.name}</option>)}
              </select>
              <input className="input-base" placeholder="Qualification (e.g. MBBS, MD) *" value={form.qualification} onChange={update("qualification")} data-testid="reg-qualification" />
              <input className="input-base" placeholder="Medical registration number *" value={form.registration_number} onChange={update("registration_number")} data-testid="reg-regnum" />
              <input className="input-base" placeholder="Languages spoken (comma separated)" value={form.languages} onChange={update("languages")} data-testid="reg-languages" />
            </div>
          </fieldset>

          <fieldset className="grid gap-4">
            <legend className="font-display font-semibold text-lg">Clinic details</legend>
            <div className="grid sm:grid-cols-2 gap-4">
              <input className="input-base" placeholder="Clinic name *" value={form.clinic_name} onChange={update("clinic_name")} data-testid="reg-clinic-name" />
              <select className="input-base" value={form.area_slug} onChange={update("area_slug")} data-testid="reg-area">
                <option value="">Area in Pune *</option>
                {areas.map((a) => <option key={a.slug} value={a.slug}>{a.name}</option>)}
              </select>
              <input className="input-base sm:col-span-2" placeholder="Full clinic address *" value={form.address} onChange={update("address")} data-testid="reg-address" />
              <input className="input-base tabular" type="number" min="0" placeholder="Consultation fee (₹)" value={form.consultation_fee} onChange={update("consultation_fee")} data-testid="reg-fee" />
              <input className="input-base" placeholder="Clinic timings (e.g. Mon–Sat 10–7)" value={form.timings} onChange={update("timings")} data-testid="reg-timings" />
              <input className="input-base sm:col-span-2" placeholder="Services offered (comma separated)" value={form.services} onChange={update("services")} data-testid="reg-services" />
            </div>
          </fieldset>

          <fieldset className="grid gap-4">
            <legend className="font-display font-semibold text-lg">Profile</legend>
            <input className="input-base" placeholder="Profile photo URL (optional)" value={form.photo_url} onChange={update("photo_url")} data-testid="reg-photo" />
            <textarea className="input-base" rows={4} placeholder="A short bio patients will see on your profile (optional)" value={form.about} onChange={update("about")} data-testid="reg-about" />
          </fieldset>

          <p className="text-xs text-muted-foreground">
            By submitting, you agree that LocalDoctor.in may review and verify your credentials before publishing your profile. Profiles are not automatically marked verified.
          </p>

          <button type="submit" disabled={submitting} className="btn-primary justify-self-start" data-testid="reg-submit">
            {submitting ? "Submitting…" : "Submit profile for verification"}
          </button>
        </form>
      </section>
    </>
  );
}
